




import { Prop, Component, Vue, Watch } from 'vue-property-decorator'
import { Dashboard } from '@/api/model/dashboard'
import { AuthClient } from '@/api/auth-client'
import moment from 'moment'

interface AuthorizationResponse {
    dashboardId: string,
    key: string,
    token: string,
    expiry: string,
}

@Component
export default class CumulioReport extends Vue {
    @Prop({ required: true }) readonly dashboard!: Dashboard
    auth: AuthorizationResponse = null
    authTokenRefreshTimeout = null

    async mounted(): Promise<void> {
        await this.injectScript()
        await this.fetchAuhToken()
        await this.createDashboard()
    }

    async beforeDestroy(): Promise<void> {
        this.unscheduleAuthTokenRefresh()
        await this.destroyDashboard()
    }

    async fetchAuhToken(): Promise<void> {
        const client = new AuthClient().axiosClient
        const basePath = process.env.VUE_APP_KEYVUE_BACKEND_BASEPATH || ''
        const response = await client.post(`${basePath}/service/cumulio/authorization`, {
            dashboardId: this.dashboard.cumulioId,
        })

        this.auth = response.data
    }

    @Watch('auth.token')
    updateAuthToken(newToken, oldToken): void {
        if (!oldToken) {
            return
        }

        Cumulio.setAuthorization(
            this.auth.key,
            this.auth.token,
            {
                dashboardId: this.auth.dashboardId,
                container: this.container,
            },
        )

        Cumulio.reloadDashboard()
    }

    @Watch('auth.expiry')
    scheduleAuthTokenRefresh(): void {
        this.unscheduleAuthTokenRefresh()

        const now = moment()
        const expiry = moment(this.auth.expiry)
        const expiryDuration = moment.duration(expiry.diff(now))

        this.authTokenRefreshTimeout = setTimeout(this.fetchAuhToken, expiryDuration.asMilliseconds() - (60 * 1000))
    }

    unscheduleAuthTokenRefresh(): void {
        if (this.authTokenRefreshTimeout) {
            clearTimeout(this.authTokenRefreshTimeout)
        }
    }

    async createDashboard(): Promise<void> {
        if (typeof Cumulio === 'undefined') {
            setTimeout(this.createDashboard, 1000)
            return
        }

        Cumulio.addDashboard({
            dashboardId: this.auth.dashboardId,
            container: this.container,
            key: this.auth.key,
            token: this.auth.token,
            language: this.$i18n.locale || 'nl',
            loader: {
                background: '#F7F8F9',
                spinnerColor: '#00A389',
                fontColor: '#333',
            },
        })
    }

    async destroyDashboard(): Promise<void> {
        await Cumulio.removeDashboard({
            container: this.container,
        })
    }

    async injectScript(): Promise<void> {
        // Skip injecting script if already done.
        if (window.keyvue.cumulioLoaded) {
            return
        }

        // Create new element and insert into head.
        const element = document.createElement('script')
        element.setAttribute('src', 'https://cdn-a.cumul.io/js/embed.min.js')
        document.head.appendChild(element)

        // Mark script as loaded.
        window.keyvue.cumulioLoaded = true
    }

    get containerId(): string {
        return 'cumulio-' + this.dashboard.id
    }

    get container(): string {
        return '#' + this.containerId
    }
}
